import {
  contentLang,
  formatAddressField,
  formatNameField,
  type ShipToAddress,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card } from '@mypoz/ui';

import { CopyableField } from './CopyableField.js';
import { StorageTermsBlock } from './StorageTermsBlock.js';

export interface WarehouseSectionProps {
  address: ShipToAddress;
  customerCode: string;
}

/**
 * One warehouse, laid out as the legacy page lays it out: a titled section, then 地址 / 邮政编码 /
 * 姓名 / 电话号码 as separate copyable rows, then 备注 and the warehouse's 注意事项.
 *
 * The structure is legacy's because it is right for the task; the clothes are ours — Card,
 * tokens, 44px targets — because the rest of the app is.
 *
 * Every value below is the vendor's own (MYP-30). The remark and the notices used to be hand-kept
 * catalog copy keyed per warehouse, which was the right call while the endpoint was unread: it let
 * all three languages say something. It is the wrong call now that the vendor sends both — its
 * text is the authoritative one, it is what the legacy page shows, and the hand-kept version had
 * already drifted (it invented three notices for Guangzhou, which in fact publishes none).
 */
export function WarehouseSection({ address, customerCode }: WarehouseSectionProps) {
  const { t } = useTranslation();
  const lang = contentLang(address.country);

  return (
    <Card className="p-0">
      <div className="border-b border-line px-5 py-4 sm:px-6">
        <h2 className="flex items-center gap-2.5 text-base font-semibold text-ink">
          <span aria-hidden="true" className="h-4 w-1 shrink-0 rounded-full bg-brand" />
          {address.label}
        </h2>
        {address.labelNative ? (
          <p lang="zh" className="mt-1 pl-3.5 text-sm text-muted">
            {address.labelNative}
          </p>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 px-5 py-4 sm:px-6">
        <CopyableField
          label={t('warehouses.fields.address')}
          value={formatAddressField(address, customerCode)}
          lang={lang}
          highlight={customerCode}
        />
        <CopyableField label={t('warehouses.fields.postcode')} value={address.postcode} />
        <CopyableField
          label={t('warehouses.fields.name')}
          value={formatNameField(address, customerCode)}
          lang={lang}
          highlight={customerCode}
        />
        <CopyableField label={t('warehouses.fields.phone')} value={address.phone} />

        {address.remark ? (
          <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4">
            <span className="shrink-0 text-sm text-muted sm:w-28">
              {t('warehouses.fields.remark')}
            </span>
            <p className="min-w-0 flex-1 text-sm break-words text-muted">{address.remark}</p>
          </div>
        ) : null}
      </div>

      <StorageTermsBlock
        freeDays={address.freeStorageDays}
        destroyAfterDays={address.destroyAfterDays}
      />

      {/*
        The rules a customer is told they must read. They differ per origin, which is why they
        cannot be one shared sentence — and why they arrive per warehouse rather than being written
        here.

        An unordered list with the marker removed, not an <ol>: the vendor numbers its own lines
        ("1. ", "2. ", and a couple of unnumbered "*" asides), so list-decimal would number them a
        second time. Still a list rather than paragraphs, because that is what it is to a screen
        reader — "list, 5 items" is the useful announcement here.
      */}
      {address.notices.length > 0 ? (
        <div className="border-t border-line px-5 py-4 sm:px-6">
          <h3 className="text-sm font-semibold text-danger">{t('warehouses.notices.title')}</h3>
          <ul className="mt-2 space-y-1.5 text-sm text-muted">
            {address.notices.map((notice) => (
              <li key={notice}>{notice}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
